/**
 * Pure diff algorithm for spec recompile.
 *
 * Given an EmittedWorkflow (from the current spec) and a LiveWorkflow
 * snapshot (from `GET /workflows/:id/with-details`), produce an ordered
 * list of operations that, when applied via the per-node/per-edge REST
 * endpoints, makes the live workflow match the spec.
 *
 * No HTTP, no fs — `sync.ts` applies the operations.
 *
 * @module spec/diff
 */

import type { EmittedWorkflow } from './emit.js';
import { authoredNodeHash } from './node-hash.js';
import { defaultEdgesNeedReconcile } from './edge-reconcile.js';
import { NodeTypeMutationError } from './diff-types.js';
import type { DiffResult, LiveNode, LiveWorkflow, SpecOp } from './diff-types.js';
import { diffWorkflowFields } from './workflow-field-diff.js';

export {
  NodeTypeMutationError,
  type DiffResult,
  type LiveEdge,
  type LiveEnvironmentConstraints,
  type LiveNode,
  type LiveWorkflow,
  type SpecOp,
  type WorkflowFieldChanges,
} from './diff-types.js';
export { hasAnyChange, summarize, type DiffSummary } from './diff-summary.js';
export { reconcileLinearEdges, type EdgeOp, type EdgeReconcileResult } from './edge-reconcile.js';

/* ── Main entry point ─────────────────────────────────────────────────── */

export function diffWorkflow(spec: EmittedWorkflow, live: LiveWorkflow): DiffResult {
  const ops: SpecOp[] = [];
  const duplicateSpecIds: string[] = [];
  const orphanedSpecIds: string[] = [];

  // Build a lookup from live node ID → live node.
  const liveById = new Map<string, LiveNode>();
  for (const n of live.nodes) liveById.set(n.id, n);

  // Track which live IDs the spec consumes so the leftovers become DELETEs.
  const consumedLiveIds = new Set<string>();
  // Track spec IDs we've already paired so duplicates trigger a fresh CREATE.
  const seenSpecIds = new Set<string>();
  spec.nodes.forEach((specNode, specIndex) => {
    let pairedLive: LiveNode | undefined;

    if (specNode.nodeId) {
      if (seenSpecIds.has(specNode.nodeId)) {
        // Duplicate `@id` from copy-paste — treat as a brand-new node.
        duplicateSpecIds.push(specNode.nodeId);
      } else {
        seenSpecIds.add(specNode.nodeId);
        const candidate = liveById.get(specNode.nodeId);
        if (candidate && !consumedLiveIds.has(candidate.id)) {
          pairedLive = candidate;
          consumedLiveIds.add(candidate.id);
        } else {
          // The spec carries an ID that doesn't exist on the live workflow
          // (orphan — workflow was rebuilt, ID stale). Treat as CREATE; the
          // writeback step will replace the stale ID with the fresh UUID.
          orphanedSpecIds.push(specNode.nodeId);
        }
      }
    } else if (specNode.nodeType === 'login') {
      // The leading login node is synthesized by `emit` from the `actor`
      // frontmatter, so it never carries an `@id`. Pair it positionally with
      // the first unconsumed live login so an unedited pull round-trips clean
      // (login nodes are never cached — a config change is harmless).
      const liveLogin = live.nodes.find(
        (n) => n.nodeType === 'login' && !consumedLiveIds.has(n.id)
      );
      if (liveLogin) {
        pairedLive = liveLogin;
        consumedLiveIds.add(liveLogin.id);
      }
    }

    if (pairedLive) {
      if (pairedLive.nodeType !== specNode.nodeType) {
        throw new NodeTypeMutationError(pairedLive.id, pairedLive.nodeType, specNode.nodeType);
      }
      // Compare authored identity, not raw configJson: a live node carries
      // runtime-only fields (playbookSteps, pageUrl, guidance) the spec can't
      // express, so a raw deep-compare would flag every run node as changed
      // and bust its replay cache on recompile. Hash equality means "same
      // authored intent" → no update → cache preserved.
      const configChanged =
        authoredNodeHash(pairedLive) !==
        authoredNodeHash({ nodeType: specNode.nodeType, configJson: specNode.configJson });
      ops.push({
        kind: 'update',
        nodeId: pairedLive.id,
        specIndex,
        nodeType: specNode.nodeType,
        configJson: specNode.configJson,
        changes: {
          configChanged,
        },
      });
    } else {
      ops.push({
        kind: 'create',
        specIndex,
        nodeType: specNode.nodeType,
        configJson: specNode.configJson,
        sourceLine: specNode.sourceLine,
      });
    }
  });

  // Anything left on the live side is a DELETE.
  for (const liveNode of live.nodes) {
    if (!consumedLiveIds.has(liveNode.id)) {
      ops.push({
        kind: 'delete',
        nodeId: liveNode.id,
        nodeType: liveNode.nodeType,
      });
    }
  }

  const { workflowFieldChanges, nameDrift } = diffWorkflowFields(spec, live);

  return {
    ops,
    workflowFieldChanges,
    edgeChanges: defaultEdgesNeedReconcile(ops, live.edges),
    nameDrift,
    duplicateSpecIds,
    orphanedSpecIds,
  };
}
